import { Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { TChatbotMode } from '../types/chatbot.types';

@Injectable({ providedIn: 'root' })
export class ChatbotLifecycleService {
  readonly #mode = new BehaviorSubject<TChatbotMode>('disabled');
  #isTerminated = false;

  /** For the session service, which has to act on a change rather than render it. */
  readonly mode$ = this.#mode.asObservable();
  readonly mode = toSignal(this.mode$, { initialValue: 'disabled' as const });

  enable() {
    this.#setMode('active');
  }

  /** Take the launcher off screen.
   * Keep the session, for transient cases like a fullscreen scanner. */
  hide() {
    this.#setMode('hidden');
  }

  /** Tear everything down.
   * For screens with no chatbot use case, like TV dashboard. */
  disable() {
    this.#setMode('disabled');
  }

  /** Use when session expired, so the socket would only be refused.
   * An open chatbot keeps its launcher and transcript on screen to read, with every action blocked.
   * One that was not on screen is torn down instead, since it has no transcript worth showing.
   * Only a fresh app start can bring the chatbot back. */
  terminate() {
    this.#isTerminated = true;
    this.#mode.next(this.#mode.value === 'active' ? 'terminated' : 'disabled');
  }

  #setMode(mode: TChatbotMode) {
    if (this.#isTerminated) return;
    this.#mode.next(mode);
  }
}
